/**
 * Resumen de Emails (`/activa/emails`): cómo les fue a las campañas de mail del comercio en el período.
 *
 * - Cuentan las campañas que mandaron algo en el rango, con TODO lo que pasó con ellas (una respuesta que llegó el día
 *   después también es de esa campaña). La tabla de abajo son esas campañas: lo de arriba es su suma.
 * - Arriba de todo, lo que no espera al rango: quienes pidieron la baja contestando y siguen recibiendo (de todo el
 *   comercio) y lo que hay que revisar.
 * - El rango vive en la URL (`?rango=7|30|90|mes|mes_pasado` o `?desde=&hasta=`), con el mismo selector que
 *   Estadísticas de WhatsApp. Contra el período anterior del mismo largo, si en él salió alguna campaña.
 * - Aperturas y clics sólo de las campañas que los miden (en Snov se apagan por campaña), y se dice.
 */

import { useMemo } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Mail } from "lucide-react";
import { emailsApi } from "../api";
import type { ResumenEmails } from "../types";
import { usePedido } from "../hooks/usePedido";
import { useEstadoEmails } from "../contexto";
import { Tarjeta } from "../../wa-oficial/components/Tarjeta";
import { Esqueleto, ErrorTarjeta } from "../../wa-oficial/components/estadisticas/comun";
import { ErrorRefresco } from "../components/ErrorRefresco";
import { SelectorRango } from "../../wa-oficial/components/estadisticas/SelectorRango";
import type { ClaveRango } from "../../wa-oficial/logic/estadisticas";
import { rangoDesdeParams } from "../../wa-oficial/logic/estadisticas";
import type { RangoFechas } from "../../wa-oficial/types.estadisticas";
import { useEsEquipoDeenex } from "../../campana-nueva/hooks/useEsEquipoDeenex";
import { num, plural } from "../../campana-nueva/utils/format";
import { Indicadores } from "../components/Indicadores";
import { AvisoBajas } from "../components/AvisoBajas";
import { Alertas } from "../components/Alertas";
import { Comparacion } from "../components/Comparacion";
import { GraficoDiario, GraficoHoras } from "../components/Graficos";
import { TablaCampanas } from "../components/TablaCampanas";
import { AnalisisEmails } from "../components/AnalisisEmails";
import { R_EMAILS } from "../logic/emails";

export default function ResumenEmailsPage() {
  const [sp, setSp] = useSearchParams();
  const spTexto = sp.toString();
  const { rango, clave, error: errorUrl } = useMemo(() => rangoDesdeParams(new URLSearchParams(spTexto), new Date()), [spTexto]);
  const { version } = useEstadoEmails();
  const equipo = useEsEquipoDeenex();
  const r = usePedido<ResumenEmails>(() => emailsApi.resumen(rango), `${rango.desde}|${rango.hasta}`, { refresco: version });

  const elegir = (c: ClaveRango) => setSp(new URLSearchParams({ rango: c }), { replace: true });
  const personalizado = (x: RangoFechas) => setSp(new URLSearchParams({ desde: x.desde, hasta: x.hasta }), { replace: true });
  const d = r.dato;

  return (
    <div className="space-y-6">
      <header className="space-y-3">
        <div>
          <h2 className="text-xl md:text-2xl font-bold text-slate-900 tracking-tight">Cómo les fue a los mails</h2>
          <p className="text-sm text-slate-500 mt-1">Las campañas de Snov que mandaron mails en estos días, y todo lo que pasó con ellas</p>
        </div>
        <SelectorRango clave={clave} rango={rango} onElegir={elegir} onPersonalizado={personalizado} />
        {errorUrl && <p className="text-xs text-amber-700" role="status">{errorUrl}</p>}
        {d && r.error && <ErrorRefresco error={r.error} onReintentar={r.recargar} />}
      </header>

      {!d ? (
        r.error ? <ErrorTarjeta texto={`No pudimos traer el resumen. ${r.error}`} onReintentar={r.recargar} /> : <Esqueleto alto="h-64" etiqueta="Cargando el resumen" />
      ) : d.totalCampanas === 0 ? (
        <div className="rounded-2xl border-2 border-dashed border-slate-200 p-10 text-center">
          <Mail size={22} className="mx-auto text-slate-300" />
          <p className="mt-3 text-sm text-slate-600">Todavía no hay campañas de mail de este comercio.</p>
          {equipo && (
            <p className="mt-1 text-xs text-slate-500">
              Las campañas de Snov se asignan a cada comercio por su casilla, en <Link to={`${R_EMAILS}/conexion`} className="font-semibold text-violet-700 underline">Conexión</Link>.
            </p>
          )}
        </div>
      ) : (
        <>
          <AvisoBajas pendientes={d.bajasPendientes} pidieron={d.conteos.pidieronBaja} rutaPersonas={`${R_EMAILS}/personas?filtro=pidieron_baja`} onListo={r.recargar} />
          {d.alertas.length > 0 && (
            <section aria-labelledby="para-revisar" className="space-y-2">
              <h3 id="para-revisar" className="text-sm font-semibold text-slate-700">Para revisar</h3>
              <Alertas alertas={d.alertas} conCampana />
            </section>
          )}
          {d.conteos.campanas === 0 ? (
            <div className="rounded-2xl border-2 border-dashed border-slate-200 p-8 text-center">
              <p className="text-sm text-slate-600">No salió ninguna campaña en estos días.</p>
              <Link to={`${R_EMAILS}/campanas`} className="mt-2 inline-block text-sm font-semibold text-violet-700 underline">
                {d.totalCampanas === 1 ? "Ver la campaña" : `Ver las ${num(d.totalCampanas)} campañas`}
              </Link>
            </div>
          ) : (
            <>
              <p className="text-sm text-slate-600">
                {num(d.conteos.campanas)} {plural(d.conteos.campanas, "campaña mandó", "campañas mandaron")} mails en estos días.
                {d.conteos.campanasSinAperturas > 0 && d.conteos.abrieron !== null && (
                  <span className="text-slate-500">
                    {" "}
                    {d.conteos.campanas - d.conteos.campanasSinAperturas === 1
                      ? "Las aperturas son de la única que las mide."
                      : `Las aperturas son de las ${num(d.conteos.campanas - d.conteos.campanasSinAperturas)} que las miden.`}
                  </span>
                )}
              </p>
              <Indicadores conteos={d.conteos} tasas={d.tasas} personas={d.personas} />
              <div className="grid gap-6 lg:grid-cols-5">
                <Tarjeta titulo="Por día" sub="Lo que pasó cada día con esas campañas" className="lg:col-span-3">
                  <GraficoDiario serie={d.serie} sinAperturas={d.conteos.abrieron === null} />
                </Tarjeta>
                <Tarjeta titulo="A qué hora interactúan" sub="Aperturas, clics y respuestas por hora del día" className="lg:col-span-2">
                  <GraficoHoras porHora={d.porHora} sinAperturas={d.conteos.abrieron === null} />
                </Tarjeta>
              </div>
              {d.anterior ? (
                <Tarjeta titulo="Contra los días anteriores"
                  sub={d.rango.dias === 1 ? "El día de antes, con las campañas que mandaron ese día" : `Los ${num(d.rango.dias)} días de antes, con las campañas que mandaron en ellos`}>
                  <Comparacion conteos={d.conteos} tasas={d.tasas} anterior={d.anterior} />
                </Tarjeta>
              ) : (
                <p className="text-xs text-slate-500">
                  {d.rango.dias === 1 ? "El día anterior" : `En los ${num(d.rango.dias)} días anteriores`} no salió ninguna campaña: no hay con qué comparar.
                </p>
              )}
              <AnalisisEmails rango={rango} />
              <Tarjeta titulo="Campañas del período" sub="Cada una con todo lo que pasó desde que salió"
                right={<Link to={`${R_EMAILS}/campanas`} className="text-sm font-semibold text-violet-700 hover:underline">Ver todas</Link>}>
                <TablaCampanas filas={d.campanas} />
              </Tarjeta>
            </>
          )}
        </>
      )}
    </div>
  );
}
